import { useQueryClient } from '@tanstack/react-query';
import { SignOutIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { EddyWordmark } from '~/components/eddy-mark';
import type { AuthMeResponse } from '~/lib/use-auth-gate';

/**
 * FRONT-2096: what a signed-in tenant member WITHOUT an Eddy seat sees in
 * place of the app. They are not locked out — anything a colleague shares
 * with them opens at its own address with this very session — but the
 * server refuses every other route, so the shell has nothing to show them.
 *
 * Pure copy on `/api/auth/me`'s cached answer: the email says whose account
 * this is (the usual confusion is "am I signed in with the right one?"), and
 * the sentence names who to ask. Deliberately NOT the /auth-error page: that
 * one blames an org-level switch, and the org is fine — the person is simply
 * not on the seat list.
 */
export function NoEddyAccess() {
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<AuthMeResponse>(['auth', 'me']);
  const signOut = () => {
    fetch('/api/auth/logout', { method: 'POST' }).then(() => {
      window.location.href = '/login';
    });
  };
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background p-4">
      <div className="flex w-full max-w-md flex-col gap-5 rounded-[var(--frame-radius)] border border-border bg-card p-8 text-center shadow-[var(--shadow-sm)]">
        <div className="flex justify-center">
          <EddyWordmark size={22} />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-lg font-semibold text-foreground">EDDY isn't switched on for you yet</h1>
          <p className="text-sm text-muted-foreground">
            {me?.email ? (
              <>
                You're signed in as <span className="text-foreground">{me.email}</span>.{' '}
              </>
            ) : null}
            Anything a colleague shares with you from EDDY opens with this account. Using EDDY
            itself needs access — ask your Blockbrain administrator to add you.
          </p>
        </div>
        <Button variant="outline" onClick={signOut} className="w-full gap-2">
          <SignOutIcon size={16} weight="bold" />
          Sign out
        </Button>
      </div>
    </div>
  );
}
